/**
 * SessionLeadersTab — MED-906 / E4.0c
 *
 * Renders the Session Leaders schedule grid for peer_led cohorts. Replaces the
 * Coaches tab on the Cohort Detail page via conditional rendering in
 * CohortDetailPage based on the cohort's group_coaching modality
 * coaching_sub_type.
 *
 * State machine (per the AC CTA reference table):
 *
 *   • `empty`      — no leader assignments yet. CTA: Generate Rotation Order.
 *   • `proposal`   — a generated rotation is in memory; cells render the
 *                    proposed leaders italicized with a tinted background.
 *                    CTAs: Regenerate, Finalize Assignments. Per-cell edits
 *                    update the in-memory proposal map; no DB writes happen
 *                    until Finalize.
 *   • `finalized`  — session_leader_id rows are populated. CTAs: Regenerate
 *                    (with confirm). Per-cell edits use the existing
 *                    `PUT .../leader` endpoint (MED-812) per AC15.
 *
 * AC7 availability surfacing: each group row carries a chevron toggle that
 * expands an inline drawer below the row showing each participant's
 * available_time_slots and availability_notes — informational only; the CM
 * uses it when manually adjusting assignments.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AlertCircle, AlertTriangle, ChevronDown, ChevronRight, RotateCw } from 'react-feather';
import type { CohortWithDetails } from '../../../types';
import {
  sessionLeadersApi,
  type FinalizeAssignment,
  type ParticipantRationaleEntry,
  type ParticipantRationaleGroup,
  type RotationProposalResponse,
  type SessionLeadersGridResponse,
  type SessionLeadersGridSession,
} from '../../../services/api/sessionLeaders';
import { SessionLeaderGridCell } from './sessionLeaders/SessionLeaderGridCell';
import { SessionLeadersSubNav, type SessionLeadersSubTab } from './sessionLeaders/SessionLeadersSubNav';
import { ParticipantsSubpage } from './sessionLeaders/ParticipantsSubpage';
import { formatScore } from './sessionLeaders/rationaleFormat';

interface SessionLeadersTabProps {
  cohort: CohortWithDetails;
  isActive?: boolean;
}

type TabPhase = 'empty' | 'proposal' | 'finalized';

function formatSessionDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function deriveInitialPhase(grid: SessionLeadersGridResponse | null): TabPhase {
  if (!grid) return 'empty';
  return grid.sessions.some(s => s.leader !== null) ? 'finalized' : 'empty';
}

export function SessionLeadersTab({ cohort, isActive }: SessionLeadersTabProps) {
  const [grid, setGrid] = useState<SessionLeadersGridResponse | null>(null);
  const [rationale, setRationale] = useState<ParticipantRationaleGroup[] | null>(null);
  const [subTab, setSubTab] = useState<SessionLeadersSubTab>('schedule');
  const [phase, setPhase] = useState<TabPhase>('empty');
  const [proposal, setProposal] = useState<RotationProposalResponse | null>(null);
  /** Per-session leader edits layered on top of the proposal. */
  const [proposalEdits, setProposalEdits] = useState<Map<string, string>>(new Map());
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [confirmRegenerateOpen, setConfirmRegenerateOpen] = useState(false);

  const fetchGrid = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [data, rationaleResp] = await Promise.all([
        sessionLeadersApi.getGrid(cohort.id),
        sessionLeadersApi.getParticipantsRationale(cohort.id).catch(() => null),
      ]);
      setGrid(data);
      setRationale(rationaleResp?.groups ?? null);
      setPhase(deriveInitialPhase(data));
    } catch (err) {
      const apiErr = err as Error & { status?: number; code?: string };
      const message =
        apiErr.code === 'not_peer_led'
          ? 'This cohort is not configured for peer leadership.'
          : (apiErr.message ?? 'Failed to load session leaders grid.');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [cohort.id]);

  useEffect(() => {
    if (isActive) {
      fetchGrid();
    }
  }, [isActive, fetchGrid]);

  const sessionColumns = useMemo(() => {
    if (!grid) return [] as Array<{ session_number: number; exemplar: SessionLeadersGridSession }>;
    const byNumber = new Map<number, SessionLeadersGridSession>();
    for (const s of grid.sessions) {
      if (s.session_number == null) continue;
      if (!byNumber.has(s.session_number)) {
        byNumber.set(s.session_number, s);
      }
    }
    return Array.from(byNumber.entries())
      .sort(([a], [b]) => a - b)
      .map(([session_number, exemplar]) => ({ session_number, exemplar }));
  }, [grid]);

  const cellLookup = useMemo(() => {
    const map = new Map<string, SessionLeadersGridSession>();
    if (!grid) return map;
    for (const s of grid.sessions) {
      if (s.session_number == null) continue;
      map.set(`${s.group_id}:${s.session_number}`, s);
    }
    return map;
  }, [grid]);

  /** Currently-proposed leader per session_id, layered: edits over original. */
  const proposedLeaderBySessionId = useMemo(() => {
    if (phase !== 'proposal' || !proposal) return new Map<string, string>();
    const out = new Map<string, string>();
    for (const a of proposal.assignments) {
      out.set(a.session_id, a.proposed_leader.user_id);
    }
    for (const [sessionId, leaderId] of proposalEdits) {
      out.set(sessionId, leaderId);
    }
    return out;
  }, [phase, proposal, proposalEdits]);

  const rationaleByGroupId = useMemo(() => {
    const map = new Map<string, ParticipantRationaleGroup>();
    if (!rationale) return map;
    for (const g of rationale) map.set(g.group_id, g);
    return map;
  }, [rationale]);

  /** session_id → session_number, for mapping proposal assignments to S#. */
  const sessionNumberById = useMemo(() => {
    const map = new Map<string, number>();
    if (!grid) return map;
    for (const s of grid.sessions) {
      if (s.session_number != null) map.set(s.session_id, s.session_number);
    }
    return map;
  }, [grid]);

  /**
   * Phase-aware "sessions assigned" per participant for the Participants
   * subpage (AC6). In the finalized state this reads the persisted grid; in the
   * proposal state it reads the in-memory proposed map (with edits) so the
   * subpage tracks the Schedule grid before finalize (IV2). Empty in the
   * pre-generation state.
   */
  const assignedSessionNumbersByUserId = useMemo(() => {
    const map = new Map<string, number[]>();
    const push = (userId: string, sessionNumber: number | null | undefined) => {
      if (sessionNumber == null) return;
      const list = map.get(userId) ?? [];
      list.push(sessionNumber);
      map.set(userId, list);
    };
    if (phase === 'proposal') {
      for (const [sessionId, userId] of proposedLeaderBySessionId) {
        push(userId, sessionNumberById.get(sessionId));
      }
    } else if (phase === 'finalized' && grid) {
      for (const s of grid.sessions) {
        if (s.leader) push(s.leader.user_id, s.session_number);
      }
    }
    return map;
  }, [phase, proposedLeaderBySessionId, sessionNumberById, grid]);

  /** AC7: the ⚠ "no sessions assigned" flag only applies once generated. */
  const rotationGenerated = phase !== 'empty';

  const hasGroupsWithMembers = (grid?.groups ?? []).some(g => g.members.length > 0);
  const hasSessions = sessionColumns.length > 0;
  const canGenerate = hasGroupsWithMembers && hasSessions;
  const hasExistingAssignments = (grid?.sessions ?? []).some(s => s.leader !== null);

  const onProposalCellChange = useCallback((sessionId: string, newLeaderId: string) => {
    setProposalEdits(prev => {
      const next = new Map(prev);
      next.set(sessionId, newLeaderId);
      return next;
    });
  }, []);

  const triggerGenerate = async () => {
    setGenerating(true);
    try {
      const response = await sessionLeadersApi.generateRotation(cohort.id);
      setProposal(response);
      setProposalEdits(new Map());
      setPhase('proposal');
      if (response.warnings.length > 0) {
        notifications.show({
          title: 'Rotation generated with warnings',
          message: `${response.warnings.length} group ${response.warnings.length === 1 ? 'warning' : 'warnings'} — see the alert below.`,
          color: 'warningAmber',
          icon: <AlertTriangle size={16} />,
          autoClose: 6000,
        });
      } else {
        notifications.show({
          title: 'Rotation generated',
          message:
            response.skipped_participants.length > 0
              ? `${response.assignments.length} assignments suggested; ${response.skipped_participants.length} participant(s) skipped.`
              : `${response.assignments.length} assignments suggested.`,
          color: 'medleyGreen',
          autoClose: 4000,
        });
      }
    } catch (err) {
      const apiErr = err as Error & { status?: number; code?: string };
      notifications.show({
        title: 'Could not generate rotation',
        message: apiErr.message ?? 'Generate failed.',
        color: 'dangerRed',
        icon: <AlertCircle size={16} />,
        autoClose: 8000,
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateClick = () => {
    if (hasExistingAssignments || phase !== 'empty') {
      setConfirmRegenerateOpen(true);
      return;
    }
    void triggerGenerate();
  };

  const handleConfirmRegenerate = () => {
    setConfirmRegenerateOpen(false);
    void triggerGenerate();
  };

  const handleDiscardProposal = () => {
    setProposal(null);
    setProposalEdits(new Map());
    setPhase(deriveInitialPhase(grid));
  };

  const handleFinalize = async () => {
    if (!proposal) return;
    const assignments: FinalizeAssignment[] = [];
    const seen = new Set<string>();
    // Original proposal assignments (with edits applied via proposedLeaderBySessionId)
    for (const a of proposal.assignments) {
      const leaderId = proposedLeaderBySessionId.get(a.session_id) ?? a.proposed_leader.user_id;
      assignments.push({ session_id: a.session_id, leader_id: leaderId });
      seen.add(a.session_id);
    }
    // Any edits made on sessions that weren't in the original proposal
    // (defensive — shouldn't happen since the cells are only editable on
    // proposed sessions, but the type allows it).
    for (const [sessionId, leaderId] of proposalEdits) {
      if (seen.has(sessionId)) continue;
      assignments.push({ session_id: sessionId, leader_id: leaderId });
    }

    setFinalizing(true);
    try {
      const result = await sessionLeadersApi.finalizeRotation(cohort.id, assignments);
      setGrid(result.grid);
      setPhase('finalized');
      setProposal(null);
      setProposalEdits(new Map());
      notifications.show({
        title: 'Rotation finalized',
        message: `${result.finalized_count} session leader ${result.finalized_count === 1 ? 'designation' : 'designations'} written.`,
        color: 'medleyGreen',
      });
    } catch (err) {
      const apiErr = err as Error & { status?: number; code?: string };
      notifications.show({
        title: 'Could not finalize rotation',
        message: apiErr.message ?? 'Finalize failed; no assignments were written.',
        color: 'dangerRed',
        icon: <AlertCircle size={16} />,
        autoClose: 8000,
      });
    } finally {
      setFinalizing(false);
    }
  };

  const toggleGroupExpand = (groupId: string) => {
    setExpandedGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" icon={<AlertCircle size={16} />}>
        {error}
      </Alert>
    );
  }

  if (!grid) return null;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center">
        <Title order={3}>Session Leaders</Title>
        <SessionLeadersSubNav activeSubTab={subTab} onSubTabChange={setSubTab} />
      </Group>

      {subTab === 'participants' && (
        <ParticipantsSubpage
          groups={rationale ?? []}
          assignedSessionNumbersByUserId={assignedSessionNumbersByUserId}
          rotationGenerated={rotationGenerated}
        />
      )}

      {subTab === 'schedule' && (
        <>
          <Group justify="space-between" align="flex-start">
            <Text size="sm" c="dimmed" maw={680}>
              Each cell shows the assigned peer leader for that group&apos;s session.
              {phase === 'proposal'
                ? ' Review the proposed rotation, edit cells as needed, then click Finalize to commit.'
                : " Pick a participant from the group's roster to assign or reassign a leader."}
            </Text>
            <Group gap="xs">
              {phase === 'proposal' && (
                <>
                  <Button variant="default" onClick={handleDiscardProposal} disabled={finalizing}>
                    Discard Proposal
                  </Button>
                  <Button
                    color="medleyGreen"
                    onClick={handleFinalize}
                    loading={finalizing}
                    disabled={!proposal || proposal.assignments.length === 0}
                  >
                    Finalize Assignments
                  </Button>
                </>
              )}
              <Button
                leftSection={<RotateCw size={16} />}
                variant={phase === 'proposal' ? 'default' : 'filled'}
                onClick={handleGenerateClick}
                loading={generating}
                disabled={!canGenerate}
              >
                {phase === 'empty' ? 'Generate Rotation Order' : 'Regenerate'}
              </Button>
            </Group>
          </Group>

      {!hasGroupsWithMembers && (
        <Alert color="blue" icon={<AlertCircle size={16} />}>
          Add participants to groups before assigning session leaders or generating a rotation order.
        </Alert>
      )}

      {hasGroupsWithMembers && !hasSessions && (
        <Alert color="blue" icon={<AlertCircle size={16} />}>
          No peer-led sessions are scheduled for this cohort yet.
        </Alert>
      )}

      {phase === 'proposal' && proposal && proposal.skipped_participants.length > 0 && (
        <Alert color="warningAmber" icon={<AlertTriangle size={16} />} title="Participants skipped">
          <Stack gap={4}>
            <Text size="sm">
              {proposal.skipped_participants.length}{' '}
              participant{proposal.skipped_participants.length === 1 ? '' : 's'} had no usable
              intake submission and were excluded from the rotation. Assign them manually after
              finalizing if needed.
            </Text>
            <Box>
              {proposal.skipped_participants.map(p => (
                <Text key={p.user_id} size="xs" c="dimmed">
                  • {p.first_name} {p.last_name} ({p.group_name}) —{' '}
                  {p.reason === 'no_intake_submission'
                    ? 'no intake submission'
                    : `incomplete intake (IvsE ${p.ivse_answered}/${p.ivse_total}, prep ${p.prep_answered}/${p.prep_total})`}
                </Text>
              ))}
            </Box>
          </Stack>
        </Alert>
      )}

      {phase === 'proposal' && proposal && proposal.warnings.length > 0 && (
        <Alert color="warningAmber" icon={<AlertTriangle size={16} />} title="Group warnings">
          <Stack gap={4}>
            {proposal.warnings.map((w, i) => (
              <Text key={`${w.group_id}-${i}`} size="xs">
                <strong>{w.group_name}:</strong> {w.message}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      {hasSessions && (
        <Box style={{ overflowX: 'auto' }}>
          <Table withTableBorder withColumnBorders highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th
                  style={{
                    minWidth: 200,
                    position: 'sticky',
                    left: 0,
                    background: 'var(--mantine-color-body)',
                  }}
                >
                  Group
                </Table.Th>
                {sessionColumns.map(({ session_number, exemplar }) => (
                  <Table.Th key={session_number} style={{ minWidth: 180 }}>
                    <Stack gap={2}>
                      <Text size="sm" fw={600}>
                        Session {session_number}
                      </Text>
                      {exemplar.topic_name && (
                        <Text size="xs" c="dimmed">
                          {exemplar.topic_name}
                        </Text>
                      )}
                      {exemplar.scheduled_start && (
                        <Text size="xs" c="dimmed">
                          {formatSessionDate(exemplar.scheduled_start)}
                        </Text>
                      )}
                    </Stack>
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {grid.groups.map(g => {
                const isExpanded = expandedGroupIds.has(g.group_id);
                const rationaleGroup = rationaleByGroupId.get(g.group_id);
                return (
                  <Fragment key={g.group_id}>
                    <Table.Tr>
                      <Table.Td
                        style={{
                          position: 'sticky',
                          left: 0,
                          background: 'var(--mantine-color-body)',
                        }}
                      >
                        <Group gap="xs" wrap="nowrap" align="flex-start">
                          <Tooltip
                            label={isExpanded ? 'Hide availability' : 'Show availability'}
                            withArrow
                          >
                            <ActionIcon
                              variant="subtle"
                              size="sm"
                              onClick={() => toggleGroupExpand(g.group_id)}
                              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} availability for ${g.name}`}
                            >
                              {isExpanded ? (
                                <ChevronDown size={16} />
                              ) : (
                                <ChevronRight size={16} />
                              )}
                            </ActionIcon>
                          </Tooltip>
                          <Stack gap={2}>
                            <Text size="sm" fw={600}>
                              {g.name}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {g.member_count} {g.member_count === 1 ? 'member' : 'members'}
                            </Text>
                          </Stack>
                        </Group>
                      </Table.Td>
                      {sessionColumns.map(({ session_number }) => {
                        const session = cellLookup.get(`${g.group_id}:${session_number}`);
                        if (!session) {
                          return (
                            <Table.Td key={session_number}>
                              <Text size="sm" c="dimmed">
                                —
                              </Text>
                            </Table.Td>
                          );
                        }
                        const proposedLeaderId = proposedLeaderBySessionId.get(session.session_id);
                        const isProposalCell =
                          phase === 'proposal' && proposedLeaderId !== undefined;
                        return (
                          <Table.Td key={session_number}>
                            <SessionLeaderGridCell
                              cohortId={cohort.id}
                              session={session}
                              groupMembers={g.members}
                              mode={isProposalCell ? 'proposal' : 'live'}
                              isProposal={isProposalCell}
                              proposedLeaderId={proposedLeaderId ?? null}
                              onProposalChange={onProposalCellChange}
                              onUpdated={fetchGrid}
                            />
                          </Table.Td>
                        );
                      })}
                    </Table.Tr>
                    {isExpanded && (
                      <Table.Tr>
                        <Table.Td
                          colSpan={1 + sessionColumns.length}
                          style={{ background: 'var(--mantine-color-gray-0)' }}
                        >
                          <AvailabilityDrawer
                            groupName={g.name}
                            participants={rationaleGroup?.participants ?? null}
                          />
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Fragment>
                );
              })}
            </Table.Tbody>
          </Table>
        </Box>
      )}

      <Modal
        opened={confirmRegenerateOpen}
        onClose={() => setConfirmRegenerateOpen(false)}
        title="Regenerate rotation order?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Regenerating will{' '}
            {phase === 'finalized'
              ? 'overwrite all current leader assignments, including any manual adjustments you have made.'
              : 'replace the current proposal, discarding any cell edits you have made.'}{' '}
            This cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmRegenerateOpen(false)}>
              Cancel
            </Button>
            <Button color="warningAmber" onClick={handleConfirmRegenerate} loading={generating}>
              Yes, Regenerate
            </Button>
          </Group>
        </Stack>
      </Modal>
        </>
      )}
    </Stack>
  );
}

interface AvailabilityDrawerProps {
  groupName: string;
  participants: ParticipantRationaleEntry[] | null;
}

/**
 * AC3/AC4: refined read-only drawer. Columns are now Participant | IvsE |
 * Availability notes — the misleading "Available time slots" column is gone
 * and IvsE (the primary rotation sort signal) is surfaced alongside the
 * free-text availability note so the CM has the key rationale at a glance while
 * adjusting assignments. Still purely informational — no edit affordances.
 */
function AvailabilityDrawer({ groupName, participants }: AvailabilityDrawerProps) {
  if (!participants || participants.length === 0) {
    return (
      <Text size="xs" c="dimmed">
        No participant data on file for {groupName}.
      </Text>
    );
  }
  return (
    <ScrollArea.Autosize mah={240}>
      <Paper p="xs" radius="sm" withBorder>
        <Stack gap={6}>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Availability — {groupName}
          </Text>
          <Table fz="xs" withRowBorders={false}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 180 }}>Participant</Table.Th>
                <Table.Th style={{ width: 80 }}>IvsE</Table.Th>
                <Table.Th>Availability notes</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {participants.map(p => (
                <Table.Tr key={p.user_id}>
                  <Table.Td>
                    <Text size="xs" fw={500}>
                      {p.first_name} {p.last_name}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{formatScore(p.ivse_average)}</Text>
                  </Table.Td>
                  <Table.Td>
                    {p.availability_notes ? (
                      <Text size="xs">{p.availability_notes}</Text>
                    ) : (
                      <Text size="xs" c="dimmed">
                        —
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Text size="xs" c="dimmed">
            Read-only · IvsE scale -2 (introvert) to +2 (extrovert)
          </Text>
        </Stack>
      </Paper>
    </ScrollArea.Autosize>
  );
}

export default SessionLeadersTab;
